import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Toast, { VARIANT } from '@nykaa/ui-components/Toast';
import { updateMyProfileData } from '@nykaa/my-profile-shared/store/action';
import EmailProfile from '@nykaa/auth/components/ProfileSection/EmailInfo';
import {
  getRegisterWelcomeMessage,
  getSplittedName,
} from '@nykaa/auth-shared/utils';
import { deleteCookie } from '@nykaa/utils/cookies';
import {
  COUNTER_DURATION_IN_SECONDS,
  EMAIL_VERIFIED_KEY,
  PROFILE_UPDATE_SUCCESS_MSG,
  RESTRICTED_ERROR_CODE,
  RETRIES_EXHAUSTED,
} from '@nykaa/my-profile-shared/constants';
import { handleVerifyEmail } from '@nykaa/my-profile-shared/utils/EventHandlers';
import {
  PROFILE_SCREEN_TITLE,
  SUBTITLES,
  TOAST_TIMER,
} from '@nykaa/auth-shared/constants';
import useAuthData from '@nykaa/auth-shared/hooks/useAuthData';
import { hideProfile } from 'store/app/actions';
import { State } from 'store/types';
import useEmailSuggestion from 'layouts/Auth/hooks/emailSuggestion';
import {
  deleteProfileDetails,
  getProfileDetails,
} from '@nykaa/my-profile-shared/utils';
import AuthBottomSheet from '@nykaa/auth/components/AuthBottomSheet';
import { NAVIGATION } from '@nykaa/ui-components/BottomSheet';
import OtpBottomSheet from '@nykaa/my-profile-shared/components/OtpBottomSheet';
import {
  resendEmailOtp,
  sendEmailOtp,
  verifyMappingOtp,
} from 'User/store/emailVerification/service';
import { getEligibleForVariant } from 'helpers/experimentV2';
import { AB_EMAIL_VERIF_REVAMP } from 'constants/experiments';

const EmailInfo = () => {
  const [toastMessage, setToastMessage] = useState('');
  const [showEmailOtpModal, setShowEmailOtpModal] = useState(false);
  const [emailVal, setEmailVal] = useState('');
  const { emailSuggestionList = {} } = useEmailSuggestion();
  const showProfile = useSelector(
    (state: State) => state.appReducer?.showProfile
  );
  const isDataFetched = useSelector((state: State) => state.auth?.dataFetched);
  const { error, message, loading } = useAuthData();
  const dispatch = useDispatch();

  const [resendTimer, setResendTimer] = useState(COUNTER_DURATION_IN_SECONDS);
  const [sendOtpErrMsg, setSendOtpErrMsg] = useState('');
  const [otpExhausted, setOtpExhausted] = useState(false);
  const [otpId, setOtpId] = useState('');

  const isEmailRevampEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_EMAIL_VERIF_REVAMP, 'A')
  );

  const handleClose = () => {
    dispatch(hideProfile());
  };

  useEffect(() => {
    if (
      !showProfile &&
      isDataFetched &&
      document.referrer.includes('user/email_verification')
    ) {
      const userData = getProfileDetails();
      const updated = userData.updated || {};
      if (updated && Object.keys(updated).length) {
        updateMyProfileData(updated)(dispatch).then((res) => {
          const { success, message: responseMessage } = res || {};
          if (success) {
            setToastMessage(PROFILE_UPDATE_SUCCESS_MSG);
          } else {
            setToastMessage(responseMessage);
          }
          deleteProfileDetails();
        });
      }
    }
  }, [dispatch, showProfile, isDataFetched]);

  const handleOtpErrorHandling = (err: any) => {
    setSendOtpErrMsg(err?.messageToDisplay);
    setToastMessage(err?.messageToDisplay);
    setResendTimer(0);
    switch (err?.type) {
      case RETRIES_EXHAUSTED:
        setOtpExhausted(true);
        break;
      default:
        break;
    }
  };

  const handleEmailOtpFlow = async (email: string): Promise<void> => {
    try {
      const otpResponse = await sendEmailOtp({ email });

      if (otpResponse?.error) {
        handleOtpErrorHandling(otpResponse.error);
        return;
      }

      setSendOtpErrMsg('');
      setOtpId(otpResponse.otpId || '');
      setShowEmailOtpModal(true);
    } catch (err) {
      handleOtpErrorHandling({ messageToDisplay: 'Failed to send OTP' });
    }
  };

  const handleFailedUpdate = (
    responseError: number,
    responseMessage: string,
    firstName: string,
    lastName: string,
    email: string
  ) => {
    if (responseError === RESTRICTED_ERROR_CODE) {
      deleteCookie(EMAIL_VERIFIED_KEY);
      const updated = {
        firstname: firstName,
        lastname: lastName,
      };
      handleVerifyEmail(email, updated);
    } else {
      setToastMessage(responseMessage);
    }
  };

  const handleSuccessfulUpdate = (firstName: string, email: string) => {
    if (!isEmailRevampEnabled) {
      setToastMessage(getRegisterWelcomeMessage(firstName));
      handleClose();
    } else {
      handleEmailOtpFlow(email);
    }
  };

  const handleUpdate = async (name: string, email: string): Promise<void> => {
    try {
      setEmailVal(email);
      const { firstName, lastName } = getSplittedName(name);
      const profileParams = {
        firstname: firstName,
        lastname: lastName,
        ...(isEmailRevampEnabled ? {} : { email }),
      };
      const response = await updateMyProfileData(profileParams)(dispatch);
      setToastMessage('');

      if (!response) {
        throw new Error('No response received from update profile');
      }

      const {
        success,
        error: responseError,
        message: responseMessage,
      } = response;

      if (success) {
        handleSuccessfulUpdate(firstName, email);
      } else {
        handleFailedUpdate(
          responseError,
          responseMessage,
          firstName,
          lastName,
          email
        );
      }
    } catch (err) {
      setToastMessage('An unexpected error occurred. Please try again.');
    }
  };

  const handleOtpSheetclose = () => {
    setShowEmailOtpModal(false);
  };

  const handlePostEmailVerification = () => {
    setToastMessage(getRegisterWelcomeMessage());
    handleClose();
    setShowEmailOtpModal(false);
  };

  return (
    <>
      {!!showProfile && (
        <AuthBottomSheet
          isLoading={loading}
          handleNavigation={handleClose}
          showOverlay
          navigation={NAVIGATION.back}
          height="354px"
          subTitle={SUBTITLES.profileScreen}
          title={PROFILE_SCREEN_TITLE}
        >
          <EmailProfile
            handleLogin={handleUpdate}
            emailSuggestionList={emailSuggestionList}
            errorText={error ? message : ''}
            isNudge={true}
          />
        </AuthBottomSheet>
      )}
      {!!toastMessage && (
        <Toast
          timer={TOAST_TIMER}
          variant={VARIANT.single}
          message={toastMessage}
          onDismiss={() => setToastMessage('')}
        />
      )}

      {showEmailOtpModal && (
        <OtpBottomSheet
          emailVal={emailVal}
          handleNavigationClose={handleOtpSheetclose}
          sendEmailOtp={sendEmailOtp}
          resendEmailOtp={resendEmailOtp}
          verifyOtp={verifyMappingOtp}
          handlePostEmailVerification={handlePostEmailVerification}
          navigationType={NAVIGATION.back}
          height="355px"
          resendTimer={resendTimer}
          sendOtpErrMsg={sendOtpErrMsg}
          otpExhausted={otpExhausted}
          otpId={otpId}
          setResendTimer={setResendTimer}
          setSendOtpErrMsg={setSendOtpErrMsg}
          setOtpExhausted={setOtpExhausted}
          setOtpId={setOtpId}
        />
      )}
    </>
  );
};

export default EmailInfo;
